import { Formik, Form } from 'formik';

import { GpusList } from '@/react/docker/host/SetupView/GpusList';
import { TimeWindowPickerInputGroup } from '@/react/portainer/environments/common/TimeWindowPicker/TimeWindowPickerInputGroup';

import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { SwitchField } from '@@/form-components/SwitchField';
import { TextTip } from '@@/Tip/TextTip';
import { FormActions } from '@@/form-components/FormActions';

import { DockerSetupFormData } from './types';

interface Props {
  initialValues: DockerSetupFormData;
  onSubmit: (values: DockerSetupFormData) => void | Promise<void>;
  /** Mode: 'create' | 'edit' */
  mode: 'create' | 'edit';
  /** Timezone for change window configuration */
  timeZone?: string;
  /** Callback when timezone changes */
  onChangeTimeZone?: (timeZone: string) => void;
  /** Environment type (docker, swarm, podman) */
  environmentType?: string;
  /** Whether the policy name has been provided (for validation) */
  nameProvided: boolean;
  /** Whether the environment groups have been provided (for validation) */
  environmentGroupsProvided: boolean;
}

/**
 * Docker Setup Policy Form Component
 *
 * Renders the setup configuration form for Docker/Swarm/Podman policies.
 * Can be used for both creating new policies and editing existing ones.
 */
export function DockerSetupPolicyForm({
  initialValues,
  onSubmit,
  mode,
  nameProvided,
  timeZone = 'UTC',
  onChangeTimeZone = () => {},
  environmentType = 'docker',
  environmentGroupsProvided,
}: Props) {
  return (
    <Formik<DockerSetupFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ values, setFieldValue, isValid, isSubmitting }) => (
        <Form>
          {/* Host and Filesystem */}
          <FormSectionTitle>Host and Filesystem</FormSectionTitle>

          <div className="form-group">
            <div className="col-sm-12">
              <TextTip className="mb-2">
                The environment must be{' '}
                <a href="https://docs.portainer.io/start/agent">
                  running the Portainer Agent
                </a>{' '}
                to use this functionality, and the root of the host must be
                bind-mounted to <strong>/host</strong> in the agent deployment.
                Check{' '}
                <a href="https://docs.portainer.io/user/docker/host/setup#enable-host-management-features">
                  our documentation
                </a>{' '}
                for more information.
              </TextTip>
            </div>
          </div>

          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                checked={values.enableHostManagementFeatures}
                onChange={(value) =>
                  setFieldValue('enableHostManagementFeatures', value)
                }
                label="Enable host management features"
                tooltip="Enable host management features: host system browsing and advanced host details."
                labelClass="col-sm-7 col-lg-4"
                data-cy="enable-host-management-switch"
              />
            </div>
          </div>

          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                checked={values.allowVolumeBrowserForRegularUsers}
                onChange={(value) =>
                  setFieldValue('allowVolumeBrowserForRegularUsers', value)
                }
                label="Enable volume management for non-administrators"
                tooltip="When enabled, regular users will be able to use Portainer volume management features."
                labelClass="col-sm-7 col-lg-4"
                data-cy="allow-volume-browser-switch"
              />
            </div>
          </div>

          {/* Other */}
          <FormSectionTitle>Other</FormSectionTitle>

          {environmentType === 'docker' && (
            <div className="form-group">
              <div className="col-sm-12">
                <SwitchField
                  checked={values.enableGpuManagement}
                  onChange={(value) =>
                    setFieldValue('enableGpuManagement', value)
                  }
                  label="Show GPU in the UI"
                  tooltip="This allows managing of GPUs for container/stack hardware acceleration via the Portainer UI. GPU support is currently limited to NVIDIA graphics cards only."
                  labelClass="col-sm-7 col-lg-4"
                  data-cy="enable-gpu-management-switch"
                />
              </div>

              {values.enableGpuManagement && (
                <div className="col-sm-12">
                  <div className="pl-4">
                    <GpusList
                      value={values.gpus}
                      onChange={(value) => setFieldValue('gpus', value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                checked={values.enableImageNotification}
                onChange={(value) =>
                  setFieldValue('enableImageNotification', value)
                }
                label="Show image up to date indicators for Stacks, Services and Containers"
                labelClass="col-sm-7 col-lg-4"
                data-cy="enable-image-notification-switch"
              />
            </div>
          </div>

          {/* Change window settings */}
          <FormSectionTitle>Change window settings</FormSectionTitle>

          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                checked={values.changeWindow.Enabled}
                onChange={(value) =>
                  setFieldValue('changeWindow', {
                    ...values.changeWindow,
                    Enabled: value,
                  })
                }
                label="Enable Change Window"
                tooltip="GitOps updates to stacks or applications outside the defined change window will not occur."
                labelClass="col-sm-7 col-lg-4"
                data-cy="enable-change-window-switch"
              />
            </div>
          </div>

          {values.changeWindow.Enabled && (
            <>
              <div className="form-group">
                <div className="col-sm-12">
                  <TimeWindowPickerInputGroup
                    values={values.changeWindow}
                    onChangeTimeZone={onChangeTimeZone}
                    onChangeChangeWindow={(changeWindow) =>
                      setFieldValue('changeWindow', changeWindow)
                    }
                    timeZone={timeZone}
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="col-sm-12">
                  <span className="text-warning small flex gap-1 align-top">
                    <div className="icon icon-sm">
                      <i className="fa fa-exclamation-triangle" />
                    </div>
                    <div>
                      GitOps updates to stacks or applications outside the
                      defined change window will not occur.
                    </div>
                  </span>
                </div>
              </div>
            </>
          )}

          <FormActions
            submitLabel={mode === 'create' ? 'Create policy' : 'Update policy'}
            loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
            isLoading={isSubmitting}
            isValid={isValid && !!nameProvided && !!environmentGroupsProvided}
            data-cy="policy-form-actions"
          />
        </Form>
      )}
    </Formik>
  );
}
